import { ref } from 'vue';

import { INVITE_CODE_STORAGE_KEY, INVITE_EMAIL_STORAGE_KEY } from '@/constants/tokens';
import { getQueryParamInviteCode } from '@/helpers/helpers.js';
import {
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemToLocalStorage,
} from '@/helpers/localStorageServices.js';

export const INVITE_EMAIL_MISMATCH_CODE = 'invite_email_mismatch';
export const INVITE_EMAIL_FAILED_CODE = 'invite_email_failed';

// Codes accepted in this tab. The invite banner hides them even if a stale /users/me still lists
// them (an accept is not always followed by a user refresh).
export const acceptedInviteCodes = ref([]);

const normalizeEmail = (value) => String(value ?? '').trim().toLowerCase();

// True only when both addresses are known and differ (case-insensitive, trimmed). An unknown
// address on either side is never a mismatch: the backend stays the judge in that case.
export const isInviteEmailMismatch = (currentEmail, invitedEmail) => {
  const current = normalizeEmail(currentEmail);
  const invited = normalizeEmail(invitedEmail);
  return !!current && !!invited && current !== invited;
};

// Error body code from the invite endpoints. Error bodies are not case-converted by the manager
// interceptor (only success bodies are), so read both spellings.
export const getInviteErrorCode = (error) => {
  const data = error?.response?.data;
  return data?.error_code ?? data?.errorCode ?? data?.detail?.error_code ?? null;
};

// Persist an invite code the moment it appears in the URL (?inviteCode). Called once on app
// load (installAuth) so the code survives the register -> login redirects and is still
// available to accept the invite once the user is authenticated. The invited email (?email) is
// stored with it; a new link without an email drops a stale one from an earlier link.
export const captureInviteFromUrl = () => {
  const code = getQueryParamInviteCode('inviteCode');
  if (code) {
    setItemToLocalStorage(INVITE_CODE_STORAGE_KEY, code);
    const email = getQueryParamInviteCode('email');
    if (email) setItemToLocalStorage(INVITE_EMAIL_STORAGE_KEY, email);
    else removeItemFromLocalStorage(INVITE_EMAIL_STORAGE_KEY);
  }
  return code || null;
};

export const getStoredInviteCode = () =>
  getItemFromLocalStorage(INVITE_CODE_STORAGE_KEY) || null;

export const getStoredInviteEmail = () =>
  getItemFromLocalStorage(INVITE_EMAIL_STORAGE_KEY) || null;

export const clearStoredInviteCode = () => {
  removeItemFromLocalStorage(INVITE_CODE_STORAGE_KEY);
  removeItemFromLocalStorage(INVITE_EMAIL_STORAGE_KEY);
};
